import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * URL del 3DS da mettere nel frame.
 *
 * `IsLightBox=True` era stato scelto perché è la variante "incastonabile" —
 * ma incastonabile riguarda solo il fatto che la pagina non rifiuta il frame
 * (nessun `X-Frame-Options`, nessun `CSP: frame-ancestors`: verificato). Il
 * LAYOUT è un'altra cosa: la lightbox è una **scatola a dimensione fissa
 * centrata**, disegnata per un overlay desktop, e dentro un frame a tutta
 * altezza su un telefono diventa un riquadrino in mezzo al bianco che
 * l'utente deve scorrere per trovare.
 *
 * Quindi si usa il link **così com'è** (`IsLightBox=False`): layout a pagina
 * intera, che riempie il frame. Il contro sarebbe che una pagina non-lightbox
 * può tentare di navigare la finestra TOP a fine 3DS — e in un'app Capacitor
 * significherebbe portarsi via l'intera app. Per quello il frame è
 * `sandbox`-ato senza `allow-top-navigation` (vedi il template): può fare
 * tutto ciò che le serve, tranne cambiare pagina sotto i piedi all'app.
 *
 * L'esito non dipende comunque da questo flag: lo dice il backend.
 */
export function embeddedThreeDsUrl(href: string): string {
  return href ?? '';
}

/**
 * Il 3DS della banca dentro l'app, in un iframe.
 *
 * I dati della carta NON passano da qui: il PAN viaggia app → nostro backend →
 * Axerve (server-to-server) già oggi, e questa pagina contiene solo il
 * plumbing 3DS (`creq`, `threeDSMethodData`) e la challenge dell'emittente.
 * Nessun campo carta nel nostro DOM.
 *
 * Il componente non sa niente di esiti: emette `frameLoad` a ogni navigazione
 * del frame — un TRIGGER per andare a chiedere al backend, non un esito.
 * L'URL del frame è cross-origin: non si legge, e non si crederebbe comunque.
 */
@Component({
  selector: 'yf-cdc-3ds',
  templateUrl: './cdc-3ds.component.html',
  styleUrls: ['./cdc-3ds.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class Cdc3dsComponent implements OnDestroy {
  $src = signal<SafeResourceUrl | null>(null);
  $loading = signal<boolean>(true);
  /**
   * La pagina della banca ci sta mettendo troppo (o non si vede nel frame:
   * capita con gli ACS che non si lasciano incastonare, e l'utente resta a
   * fissare il bianco). Da qui la via di fuga smette di essere un link
   * discreto e diventa l'azione principale, spiegata.
   */
  $slow = signal<boolean>(false);
  private slowTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * Soglia oltre la quale l'utente e' plausibilmente BLOCCATO, non solo in
   * attesa. Prima di allora la via di fuga non si mostra affatto: invitare a
   * uscire mentre la banca sta ancora rispondendo fa abbandonare un flusso che
   * stava funzionando. Una challenge lenta puo' richiedere parecchio.
   */
  private static readonly SLOW_AFTER_MS = 30000;

  @Input() set url(value: string | null | undefined) {
    this.$loading.set(true);
    this.$slow.set(false);
    this.$src.set(value ? this.sanitizer.bypassSecurityTrustResourceUrl(value) : null);
    this.armSlowTimer(!!value);
  }

  private armSlowTimer(active: boolean): void {
    if (this.slowTimer) { clearTimeout(this.slowTimer); this.slowTimer = null; }
    if (!active) return;
    this.slowTimer = setTimeout(() => this.$slow.set(true), Cdc3dsComponent.SLOW_AFTER_MS);
  }

  ngOnDestroy(): void {
    if (this.slowTimer) { clearTimeout(this.slowTimer); this.slowTimer = null; }
  }

  /** Il frame ha navigato: il chiamante vada a chiedere l'esito al backend. */
  @Output() frameLoad = new EventEmitter<void>();
  /** L'utente ha chiuso: si risolve comunque l'esito prima di dire "annullato". */
  @Output() cancelled = new EventEmitter<void>();
  /** Via di fuga se la pagina non funziona nel frame (es. cookie di terze parti). */
  @Output() openInBrowser = new EventEmitter<void>();

  constructor(private readonly sanitizer: DomSanitizer) { }

  onFrameLoad(): void {
    this.$loading.set(false);
    this.frameLoad.emit();
  }
}
